import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  PORTAL_BANNER_COLOR_OPTIONS,
  resolvePortalBannerColor,
  useActivatePortalBannerMutation,
  useCreatePortalBannerMutation,
  useDeactivatePortalBannerMutation,
  useDeletePortalBannerMutation,
  useListPortalBanners,
  type PortalBannerColor,
} from '~entities/portal-banner';

import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';

const formatDate = (value?: string) => {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString();
};

export const PortalBannerSettingsPage = () => {
  const { data: banners, isFetching } = useListPortalBanners();
  const createMutation = useCreatePortalBannerMutation();
  const activateMutation = useActivatePortalBannerMutation();
  const deactivateMutation = useDeactivatePortalBannerMutation();
  const deleteMutation = useDeletePortalBannerMutation();

  const [message, setMessage] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaPath, setCtaPath] = useState('');
  const [color, setColor] = useState<PortalBannerColor>('sky');

  const handlePublish = () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return;
    }

    createMutation.mutate(
      {
        message: trimmedMessage,
        ctaLabel: ctaLabel.trim() || undefined,
        ctaPath: ctaPath.trim() || undefined,
        color,
      },
      {
        onSuccess: () => {
          setMessage('');
          setCtaLabel('');
          setCtaPath('');
          setColor('sky');
        },
      },
    );
  };

  return (
    <div className='min-w-0'>
      <Link to='/dashboard' className='flex min-w-0 items-center gap-2'>
        <CircleArrowLeft className='size-6 shrink-0' />
        <h2 className='min-w-0 text-xl font-semibold sm:text-2xl'>
          Portal Banners
        </h2>
      </Link>

      <div className='mt-4 space-y-8 border-t pt-8'>
        <section className='max-w-2xl space-y-4'>
          <h3 className='text-lg font-medium'>Publish new banner</h3>
          <p className='text-muted-foreground text-sm'>
            Publishing activates this banner for all studios that have not
            dismissed it. Any previously active banner is deactivated.
            Reactivating a banner clears dismissals so it shows again for every
            studio.
          </p>
          <div className='space-y-2'>
            <Label htmlFor='portal-banner-message'>Message</Label>
            <Input
              id='portal-banner-message'
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
              }}
              placeholder='Ready for a seamless migration?'
              maxLength={500}
            />
          </div>
          <div className='grid gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='portal-banner-cta-label'>CTA label</Label>
              <Input
                id='portal-banner-cta-label'
                value={ctaLabel}
                onChange={(event) => {
                  setCtaLabel(event.target.value);
                }}
                placeholder='Try the Import Wizard!'
                maxLength={100}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='portal-banner-cta-path'>CTA path</Label>
              <Input
                id='portal-banner-cta-path'
                value={ctaPath}
                onChange={(event) => {
                  setCtaPath(event.target.value);
                }}
                placeholder='/import-data'
                maxLength={500}
              />
            </div>
          </div>
          <div className='space-y-2'>
            <Label>Color</Label>
            <div className='flex flex-wrap gap-2'>
              {PORTAL_BANNER_COLOR_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type='button'
                  onClick={() => {
                    setColor(option.value);
                  }}
                  className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${
                    color === option.value
                      ? 'border-slate-900 ring-1 ring-slate-900'
                      : 'border-slate-200'
                  }`}
                >
                  <span
                    className={`size-3 rounded-full ${option.swatchClassName}`}
                  />
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <Button
            onClick={handlePublish}
            disabled={!message.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Publishing…
              </>
            ) : (
              'Publish banner'
            )}
          </Button>
        </section>

        <section className='space-y-4'>
          <h3 className='text-lg font-medium'>Announcement history</h3>
          {isFetching && !banners ? (
            <div className='flex justify-center py-8'>
              <Loader2 className='size-10 animate-spin' />
            </div>
          ) : (
            <div className='overflow-x-auto rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Message</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created by</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(banners ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className='text-muted-foreground py-8 text-center'
                      >
                        No portal banners yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (banners ?? []).map((banner) => {
                      const bannerColor = resolvePortalBannerColor(
                        banner.color,
                      );
                      const colorOption = PORTAL_BANNER_COLOR_OPTIONS.find(
                        (option) => option.value === bannerColor,
                      );
                      return (
                        <TableRow key={banner.id}>
                          <TableCell className='max-w-xs'>
                            <div className='font-medium'>{banner.message}</div>
                            {banner.ctaLabel ? (
                              <div className='text-muted-foreground text-xs'>
                                {banner.ctaLabel}
                                {banner.ctaPath ? ` → ${banner.ctaPath}` : ''}
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <span className='inline-flex items-center gap-2 text-sm'>
                              <span
                                className={`size-3 rounded-full ${colorOption?.swatchClassName ?? 'bg-sky-600'}`}
                              />
                              {colorOption?.label ?? 'Sky'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {banner.isActive ? (
                              <span className='rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800'>
                                Active
                              </span>
                            ) : (
                              <span className='rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700'>
                                Inactive
                              </span>
                            )}
                          </TableCell>
                          <TableCell className='text-sm'>
                            {banner.createdBy.email}
                          </TableCell>
                          <TableCell className='text-sm whitespace-nowrap'>
                            {formatDate(banner.createdAt)}
                          </TableCell>
                          <TableCell className='space-x-2 text-right'>
                            {banner.isActive ? (
                              <Button
                                variant='outline'
                                size='sm'
                                disabled={deactivateMutation.isPending}
                                onClick={() => {
                                  deactivateMutation.mutate(banner.id);
                                }}
                              >
                                Deactivate
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={activateMutation.isPending}
                                  onClick={() => {
                                    activateMutation.mutate(banner.id);
                                  }}
                                >
                                  Reactivate
                                </Button>
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  disabled={deleteMutation.isPending}
                                  onClick={() => {
                                    deleteMutation.mutate(banner.id);
                                  }}
                                >
                                  <Trash2 className='size-4' />
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
